import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Gem, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { StepIndicator } from "@/components/create/StepIndicator";
import { useCredits } from "@/hooks/useCredits";
import { track } from "@/lib/analytics";


export const Route = createFileRoute("/_authenticated/create/confirm")({
  head: () => ({
    meta: [
      { title: "生成内容の確認 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ConfirmPage,
});

const WIZARD_KEY = "create:wizard";
const QUALITY_KEY = "create:quality";
const WATERMARK_REMOVE_COST = 2;
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ??
  import.meta.env.VITE_SUPABASE_ANON_KEY) as string;

type TierId =
  | "trial"
  | "standard"
  | "high"
  | "premium"
  | "lipsync"
  | "i2v_standard"
  | "i2v_high"
  | "i2v_long"
  | "story_std"
  | "story_pro";

type CreateMode = "faceswap" | "i2v" | "story";

const TIER_LABEL: Record<TierId, string> = {
  trial: "お試し変身",
  standard: "標準動画生成",
  high: "高品質動画",
  premium: "本格動画（音声付き）",
  lipsync: "喋らせる（リップシンク）",
  i2v_standard: "プロンプト生成（標準）",
  i2v_high: "プロンプト生成（高品質）",
  i2v_long: "プロンプト生成（ロング）",
  story_std: "ストーリーリール（標準）",
  story_pro: "ストーリーリール（高品質）",
};

const TIER_TICKETS: Record<TierId, number> = {
  trial: 20,
  standard: 12,
  high: 40,
  premium: 50,
  lipsync: 5,
  i2v_standard: 25,
  i2v_high: 50,
  i2v_long: 90,
  story_std: 120,
  story_pro: 220,
};

const I2V_TIERS: TierId[] = ["i2v_standard", "i2v_high", "i2v_long"];
const STORY_TIERS: TierId[] = ["story_std", "story_pro"];
const NO_VIDEO_TIERS: TierId[] = [...I2V_TIERS, ...STORY_TIERS, "lipsync"];

type UploadedAsset = {
  path: string;
  signedUrl: string;
  name: string;
  size: number;
};

type WizardState = {
  mode?: CreateMode;
  image?: UploadedAsset;
  background?: UploadedAsset;
  video?: UploadedAsset & {
    durationSec: number;
    thumbnailPath?: string;
    thumbnailSignedUrl?: string;
  };
  storyShots?: Array<{ prompt: string; duration: number }>;
};

type QualityState = {
  tierId: TierId;
  removeWatermark: boolean;
  script?: string;
  videoPrompt?: string;
  aspectRatio?: "9:16" | "1:1" | "16:9";
};

function readJSON<T>(key: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function ConfirmPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [userPlan, setUserPlan] = useState<string>("free");
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const [quality, setQuality] = useState<QualityState | null>(null);

  
  const [agreedConsent, setAgreedConsent] = useState(false);
  const [title, setTitle] = useState("");
  const [agreedPolicy, setAgreedPolicy] = useState(false);

  function getDefaultTitle(): string {
    const now = new Date();
    return now.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const [submitting, setSubmitting] = useState(false);
  const [blockedModal, setBlockedModal] = useState<{
    input_image: boolean;
    input_video: boolean;
  } | null>(null);

  const balance = useCredits(userId);

  useEffect(() => {
    const w = readJSON<WizardState>(WIZARD_KEY);
    const q = readJSON<QualityState>(QUALITY_KEY);
    const isI2v = q && I2V_TIERS.includes(q.tierId);
    const noVideo = q && NO_VIDEO_TIERS.includes(q.tierId);
    if (!w?.image) {
      toast.error("先に素材をアップロードしてください");
      navigate({ to: "/create" });
      return;
    }
    if (!noVideo && !w?.video) {
      toast.error("先に素材をアップロードしてください");
      navigate({ to: "/create" });
      return;
    }
    void isI2v;
    if (!q) {
      toast.error("先に品質を選択してください");
      navigate({ to: "/create/quality" });
      return;
    }
    setWizard(w);
    setQuality(q);

    supabase.auth.getUser().then(async ({ data }) => {
      const uid = data.user?.id ?? null;
      setUserId(uid);
      if (uid) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("plan")
          .eq("user_id", uid)
          .maybeSingle();
        if (profile?.plan) setUserPlan(profile.plan);
      }
    });
  }, [navigate]);

  const totalCost = useMemo(() => {
    if (!quality) return 0;
    return TIER_TICKETS[quality.tierId] + (quality.removeWatermark ? WATERMARK_REMOVE_COST : 0);
  }, [quality]);

  const requiresCommercialAck = userPlan !== "pro";
  void requiresCommercialAck;
  const allAgreed = agreedConsent && agreedPolicy;
  const canAfford = balance !== null && balance >= totalCost;
  const canSubmit = allAgreed && canAfford && !submitting && !!wizard && !!quality;

  const handleSubmit = async () => {
    if (!canSubmit || !wizard || !quality) return;
    setSubmitting(true);
    try {
      const noVideo = NO_VIDEO_TIERS.includes(quality.tierId);
      // Sign URLs client-side
      const signImage = await supabase.storage
        .from("uploads")
        .createSignedUrl(wizard.image!.path, 60 * 60);
      if (signImage.error || !signImage.data?.signedUrl) {
        throw new Error("入力画像の署名URL生成に失敗しました");
      }
      let videoSignedUrl: string | undefined;
      let thumbnailSignedUrl: string | undefined;
      if (!noVideo) {
        const signVideo = await supabase.storage
          .from("uploads")
          .createSignedUrl(wizard.video!.path, 60 * 60);
        if (signVideo.error || !signVideo.data?.signedUrl) {
          throw new Error("入力動画の署名URL生成に失敗しました");
        }
        videoSignedUrl = signVideo.data.signedUrl;
        if (wizard.video?.thumbnailPath) {
          const signThumb = await supabase.storage
            .from("uploads")
            .createSignedUrl(wizard.video.thumbnailPath, 60 * 60);
          thumbnailSignedUrl = signThumb.data?.signedUrl;
        }
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        throw new Error("ログイン状態を確認できません。再ログインしてください。");
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-generation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          quality_tier: quality.tierId,
          input_image_url: signImage.data.signedUrl,
          input_video_url: videoSignedUrl,
          input_video_thumbnail_url: thumbnailSignedUrl,
          has_watermark: !quality.removeWatermark,
          lipsync_text: quality.script,
          video_prompt: quality.videoPrompt,
          story_shots: STORY_TIERS.includes(quality.tierId) ? wizard.storyShots : undefined,
          aspect_ratio: quality.aspectRatio,
        }),
      });
      const result = await response.json().catch(() => null);

      if (!response.ok) {
        // Special-case content moderation errors (no failed-page redirect)
        if (result?.error === "CONTENT_BLOCKED") {
          setBlockedModal({
            input_image: Boolean(result?.blocked?.input_image),
            input_video: Boolean(result?.blocked?.input_video),
          });
          toast.error("不適切なコンテンツが検出されました");
          setSubmitting(false);
          return;
        }
        if (result?.error === "MODERATION_UNAVAILABLE") {
          toast.error(
            "コンテンツ確認サービスが一時停止中です。時間を置いてお試しください。",
          );
          setSubmitting(false);
          return;
        }
        let message = "生成の開始に失敗しました";
        if (result?.error === "INSUFFICIENT_CREDITS") {
          message = "クレジットが足りません";
        } else if (result?.error === "TRIAL_LIMIT_REACHED") {
          message = "お試し変身は2回までです。別の品質を選択してください。";
        } else if (result?.error === "PROVIDER_SUBMIT_FAILED") {
          message = `動画生成サービスでエラーが発生しました: ${result.detail ?? ""}`;
        } else if (result?.error) {
          message = `${result.error}: ${result.detail ?? ""}`;
        }
        throw new Error(message);
      }

      const generationId = result?.generation_id as string | undefined;
      if (!generationId) {
        throw new Error("生成IDが返却されませんでした");
      }

      // タイトルを保存（空なら生成日時）
      const finalTitle = title.trim() || getDefaultTitle();
      await supabase
        .from("generations")
        .update({ title: finalTitle.slice(0, 100) })
        .eq("id", generationId);

      sessionStorage.removeItem(WIZARD_KEY);
      sessionStorage.removeItem(QUALITY_KEY);
      track("generation_start", {
        quality_tier: quality.tierId,
        has_watermark: !quality.removeWatermark,
        model: result?.model ?? null,
      });
      toast.success("生成を開始しました");
      navigate({
        to: "/create/processing/$id",
        params: { id: generationId },
      });
    } catch (e) {
      const reason = e instanceof Error ? e.message : "生成の開始に失敗しました";
      toast.error(reason);
      sessionStorage.removeItem(WIZARD_KEY);
      sessionStorage.removeItem(QUALITY_KEY);
      navigate({ to: "/create/failed", search: { reason } });
    }
  };

  if (!wizard || !quality) {
    return (
      <main className="min-h-screen bg-background px-4 py-8 sm:py-10">
        <div className="mx-auto max-w-3xl">
          <div className="mb-10">
            <StepIndicator current={3} />
          </div>
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </main>
    );
  }

  const afterBalance = balance !== null ? Math.max(0, balance - totalCost) : null;

  return (
    <main className="min-h-screen bg-background px-4 py-8 pb-32 sm:py-10">
      <div className="mx-auto max-w-3xl">
        <div className="mb-10">
          <StepIndicator current={3} />
        </div>

        <header className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">内容を確認</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            以下の内容で動画を生成します。間違いがなければ生成を開始してください。
          </p>
        </header>

        {/* Selection summary */}
        <Card className="p-6">
          <h2 className="mb-4 text-lg font-bold">選択内容</h2>

          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Thumb label="人物画像" src={wizard.image?.signedUrl} />
            {wizard.background && (
              <Thumb label="背景画像" src={wizard.background.signedUrl} />
            )}
            {!NO_VIDEO_TIERS.includes(quality.tierId) && wizard.video?.signedUrl && (
              <ThumbVideo label="ベース動画" src={wizard.video.signedUrl} />
            )}
          </div>

          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <Row label="品質グレード" value={TIER_LABEL[quality.tierId]} />
            {quality.aspectRatio && (
              <Row
                label="アスペクト比"
                value={
                  quality.aspectRatio === "9:16"
                    ? "縦長 (9:16)"
                    : quality.aspectRatio === "1:1"
                      ? "正方形 (1:1)"
                      : "横長 (16:9)"
                }
              />
            )}
            {quality.script && (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-muted-foreground">喋らせる内容</dt>
                <dd className="mt-1 whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-sm">
                  {quality.script}
                </dd>
              </div>
            )}
            {quality.videoPrompt && (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-muted-foreground">プロンプト</dt>
                <dd className="mt-1 whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-sm">
                  {quality.videoPrompt}
                </dd>
              </div>
            )}
            {STORY_TIERS.includes(quality.tierId) && wizard.storyShots && wizard.storyShots.length > 0 && (
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-muted-foreground">
                  シーン構成（{wizard.storyShots.length}シーン・合計{wizard.storyShots.reduce((a, s) => a + s.duration, 0)}秒）
                </dt>
                <dd className="mt-1 space-y-1.5">
                  {wizard.storyShots.map((s, i) => (
                    <div key={i} className="rounded-md border border-border bg-muted/30 p-2 text-xs">
                      <div className="font-bold text-primary">シーン {i + 1}（{s.duration}s）</div>
                      <div className="mt-0.5 whitespace-pre-wrap">{s.prompt}</div>
                    </div>
                  ))}
                </dd>
              </div>
            )}
          </dl>

          <div className="mt-6 flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 p-4">
            <span className="text-sm font-medium">消費クレジット</span>
            <span className="flex items-baseline gap-1">
              <Gem className="h-5 w-5 text-primary" />
              <span className="text-3xl font-extrabold text-primary">{totalCost}</span>
              <span className="text-sm text-muted-foreground">クレジット</span>
            </span>
          </div>
        </Card>

        {/* Title input */}
        <Card className="mt-4 p-5">
          <label htmlFor="gen-title" className="text-sm font-bold">
            動画の名前 <span className="text-xs font-normal text-muted-foreground">（無記入の場合は生成日時）</span>
          </label>
          <input
            id="gen-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
            placeholder="例：旅行ハイライト2026"
            className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </Card>

        {/* Balance preview */}
        <Card className="mt-4 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">残高の変化</span>
            <span className="font-medium">
              現在：
              <span className="mx-1 font-bold">{balance ?? "—"}</span>
              クレジット
              <span className="mx-2 text-muted-foreground">→</span>
              生成後：
              <span
                className={cn(
                  "mx-1 font-bold",
                  canAfford ? "text-primary" : "text-destructive",
                )}
              >
                {afterBalance ?? "—"}
              </span>
              クレジット
            </span>
          </div>
          {!canAfford && balance !== null && (
            <div className="mt-3 flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              <span>クレジットが足りません</span>
              <Link
                to="/credits-shop"
                className="font-semibold underline underline-offset-2"
              >
                クレジットを購入
              </Link>
            </div>
          )}
        </Card>

        {/* Acknowledgments */}
        <Card className="mt-4 p-5">
          <h2 className="mb-3 text-base font-bold">以下を確認してください</h2>
          <div className="space-y-3">
            <Ack
              checked={agreedConsent}
              onChange={setAgreedConsent}
              text="アップロードした画像の人物は私自身、または使用許諾を得ています。"
            />
            <Ack
              checked={agreedPolicy}
              onChange={setAgreedPolicy}
              text="不適切な内容（性的・暴力的・有名人等）は生成できないことを理解しました。"
            />
          </div>
        </Card>

        {/* Actions */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              "h-14 w-full max-w-md text-base font-bold sm:w-auto sm:px-12",
              "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            )}
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                送信中...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                生成を開始する
              </>
            )}
          </Button>

          <div className="flex items-center gap-4 pt-2 text-sm">
            <button
              type="button"
              onClick={() => navigate({ to: "/create/quality" })}
              disabled={submitting}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              戻る
            </button>
            <span className="text-muted-foreground">|</span>
            <Link
              to="/dashboard"
              className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              キャンセル
            </Link>
          </div>
        </div>
      </div>

      <AlertDialog
        open={!!blockedModal}
        onOpenChange={(open) => !open && setBlockedModal(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>不適切なコンテンツが検出されました</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  アップロードされた素材に、当サービスでは取り扱いできない内容が含まれている可能性があります。
                </p>
                {blockedModal && (
                  <ul className="list-disc pl-5 text-sm">
                    {blockedModal.input_image && <li>顔写真</li>}
                    {blockedModal.input_video && <li>ベース動画（最初のフレーム）</li>}
                  </ul>
                )}
                <p className="text-sm text-muted-foreground">
                  クレジットは消費されていません。別の素材で再度お試しください。
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBlockedModal(null)}>
              閉じる
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setBlockedModal(null);
                sessionStorage.removeItem(WIZARD_KEY);
                sessionStorage.removeItem(QUALITY_KEY);
                navigate({ to: "/create" });
              }}
            >
              素材を選び直す
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-semibold">{value}</dd>
    </div>
  );
}

function Thumb({ label, src }: { label: string; src?: string }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <div className="aspect-square w-full overflow-hidden rounded-lg border border-border bg-muted">
        {src ? (
          <img src={src} alt={label} className="h-full w-full object-cover" />
        ) : null}
      </div>
    </div>
  );
}

function ThumbVideo({ label, src }: { label: string; src?: string }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <div className="aspect-square w-full overflow-hidden rounded-lg border border-border bg-black">
        {src ? (
          <video src={src} className="h-full w-full object-cover" muted playsInline />
        ) : null}
      </div>
    </div>
  );
}

function Ack({
  checked,
  onChange,
  text,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  text: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md p-2 hover:bg-muted/40">
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => onChange(!!v)}
        className="mt-0.5"
      />
      <span className="text-sm leading-relaxed">{text}</span>
    </label>
  );
}
